import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import RiskBadge from './RiskBadge';
import VulnerabilityDetail from './VulnerabilityDetail';

type RiskLevel = 'critical' | 'high' | 'medium' | 'low' | 'info';

interface Vulnerability {
  id: string;
  title: string;
  severity: RiskLevel;
  description: string;
  impact?: string;
  remediation?: string;
  cwe?: string;
  file?: string;
  line?: number;
  snippet?: string;
  source?: 'sae' | 'dae' | 'aie' | 'gce' | 'zde' | 'rfia' | 'vge';
}

interface VulnerabilityGroupProps {
  title: string;
  vulnerabilities: Vulnerability[];
  defaultExpanded?: boolean;
  /** Scan target URL forwarded to each VulnerabilityDetail for GitHub linking */
  repoTarget?: string;
}

const SEVERITY_ORDER: RiskLevel[] = ['critical', 'high', 'medium', 'low', 'info'];
const INITIAL_VISIBLE = 5;

function highestSeverity(vulnerabilities: Vulnerability[]): RiskLevel {
  for (const level of SEVERITY_ORDER) {
    if (vulnerabilities.some((v) => v.severity === level)) {
      return level;
    }
  }
  return 'info';
}

const VulnerabilityGroup = ({
  title,
  vulnerabilities,
  defaultExpanded = false,
  repoTarget,
}: VulnerabilityGroupProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [highlightedVulnId, setHighlightedVulnId] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const worst = highestSeverity(vulnerabilities);

  const handleToggle = () => {
    if (isExpanded) setVisibleCount(INITIAL_VISIBLE);
    setIsExpanded((prev) => !prev);
  };

  const visibleVulnerabilities = vulnerabilities.slice(0, visibleCount);
  const hiddenCount = vulnerabilities.length - visibleCount;

  // Stable ref so the event callback never needs to be recreated on re-renders.
  // Updated via useLayoutEffect (not during render) to satisfy react-hooks/refs.
  const vulnerabilitiesRef = useRef(vulnerabilities);
  useLayoutEffect(() => {
    vulnerabilitiesRef.current = vulnerabilities;
  });

  // Stable callback — deps are empty; reads current list via ref.
  const handleExpandVulnerability = useCallback(
    (event: CustomEvent<{ vulnerabilityId: string }>) => {
      const targetId = event.detail.vulnerabilityId;
      if (!vulnerabilitiesRef.current.some((v) => v.id === targetId)) return;
      setIsExpanded(true);
      setHighlightedVulnId(targetId);
      setTimeout(() => {
        document
          .getElementById(`vuln-${targetId}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
      setTimeout(() => setHighlightedVulnId(null), 2000);
    },
    []
  );

  useEffect(() => {
    window.addEventListener('expandVulnerability', handleExpandVulnerability as EventListener);
    return () => {
      window.removeEventListener('expandVulnerability', handleExpandVulnerability as EventListener);
    };
  }, [handleExpandVulnerability]);

  return (
    <div className="border rounded-lg overflow-hidden shadow-sm">
      <button
        onClick={handleToggle}
        className="w-full flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors text-left"
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${title} group`}
      >
        {isExpanded ? (
          <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
        )}

        <span className="flex-1 font-medium">{title}</span>

        <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
          {vulnerabilities.length} {vulnerabilities.length === 1 ? 'finding' : 'findings'}
        </span>

        <RiskBadge level={worst} />
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 border-t bg-muted/20 animate-fade-in">
          <div className="pt-4 space-y-3">
            {visibleVulnerabilities.map((vuln) => (
              <VulnerabilityDetail
                key={vuln.id}
                vulnerability={vuln}
                forceExpanded={defaultExpanded}
                repoTarget={repoTarget}
                highlightedVulnerabilityId={highlightedVulnId ?? undefined}
              />
            ))}
            {hiddenCount > 0 && (
              <button
                onClick={() => setVisibleCount(vulnerabilities.length)}
                className="w-full text-sm text-muted-foreground hover:text-foreground text-center py-2 border border-dashed rounded-lg transition-colors hover:border-foreground/30"
              >
                Show {hiddenCount} more {hiddenCount === 1 ? 'finding' : 'findings'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VulnerabilityGroup;

/**
 * Group an array of vulnerabilities by title and sort groups by
 * highest severity first, then by count descending within the same severity.
 */
export function groupVulnerabilities(
  vulnerabilities: Vulnerability[]
): { title: string; vulnerabilities: Vulnerability[] }[] {
  const map = new Map<string, Vulnerability[]>();
  for (const v of vulnerabilities) {
    const existing = map.get(v.title);
    if (existing) {
      existing.push(v);
    } else {
      map.set(v.title, [v]);
    }
  }

  const groups = Array.from(map.entries()).map(([title, vulns]) => ({
    title,
    vulnerabilities: vulns,
  }));

  groups.sort((a, b) => {
    const aIdx = SEVERITY_ORDER.indexOf(highestSeverity(a.vulnerabilities));
    const bIdx = SEVERITY_ORDER.indexOf(highestSeverity(b.vulnerabilities));
    if (aIdx !== bIdx) return aIdx - bIdx;
    return b.vulnerabilities.length - a.vulnerabilities.length;
  });

  return groups;
}
